import { useQuery } from '@tanstack/react-query';
import { Expand } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import {
  FundDetailActions,
  FundDetailPanel,
  FundDetailProvider,
  type FundDetail,
} from '@/components/giving/fund-detail-panel';
import { fundTypeLabel } from '@/components/shared/fund-type-select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { hasPermission, PERMISSIONS } from '@/lib/permissions';
import { useChurchStore } from '@/stores/church-store';

export const FundDetailDialog = ({
  fund = null,
  fundId = null,
  open,
  onOpenChange,
  onEdit,
}: {
  /** Fund already loaded by the caller. Skips a refetch when present. */
  fund?: FundDetail | null;
  fundId?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit?: (fund: FundDetail) => void;
}) => {
  const { t } = useTranslation('giving');
  const { t: tCommon } = useTranslation();
  const navigate = useNavigate();
  const membership = useChurchStore((state) => state.membership);
  const canManage = hasPermission(membership, PERMISSIONS.GIVING_MANAGE_FUNDS);
  const resolvedId = fund?.id ?? fundId;

  const loaded = useQuery({
    queryKey: ['giving', 'funds', resolvedId],
    queryFn: () => api.get<FundDetail>(`/giving/funds/${resolvedId}`),
    enabled: open && Boolean(resolvedId) && !fund,
  });

  const detail = fund ?? (resolvedId ? loaded.data : null) ?? null;
  const loading = open && !detail && loaded.isFetching;

  const openFullView = (next: FundDetail) => {
    onOpenChange(false);
    void navigate(`/giving/funds/${next.id}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="2xl" closeLabel={tCommon('action.close')} className="overflow-visible">
        {detail ? (
          <FundDetailProvider fund={detail}>
            <DialogHeader>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1 space-y-1">
                  <DialogTitle className="min-w-0 truncate">{detail.name}</DialogTitle>
                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <Badge variant={detail.isActive ? 'success' : 'neutral'}>
                      {detail.isActive ? t('funds.statusActive') : t('funds.inactive')}
                    </Badge>
                    <DialogDescription>
                      {t('funds.detail.description', {
                        code: detail.code,
                        type: fundTypeLabel(detail, t),
                      })}
                    </DialogDescription>
                  </div>
                </div>
                <FundDetailActions
                  trailing={
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      leadingIcon={<Expand className="size-3.5" aria-hidden />}
                      onClick={() => openFullView(detail)}
                    >
                      {t('funds.detail.fullView')}
                    </Button>
                  }
                />
              </div>
            </DialogHeader>
            <DialogBody>
              <FundDetailPanel fund={detail} popoverPortal="dialog" />
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {tCommon('action.close')}
              </Button>
              {canManage && onEdit ? (
                <Button type="button" onClick={() => onEdit(detail)}>
                  {t('funds.edit')}
                </Button>
              ) : null}
            </DialogFooter>
          </FundDetailProvider>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{t('funds.detail.title')}</DialogTitle>
            </DialogHeader>
            <DialogBody>
              {loading ? (
                <div className="space-y-3" aria-busy>
                  <Skeleton className="h-5 w-40" />
                  <Skeleton className="h-28 w-full" />
                </div>
              ) : (
                <p className="text-body-sm text-fg-secondary">{tCommon('state.notFoundBody')}</p>
              )}
            </DialogBody>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
